import { format } from "date-fns";
import { FileSpreadsheet, FileText } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { SingleValue } from "react-select";
import { ISoldItemsExportQueued } from "src/@types/sold-items";

import { SelectInput } from "components/Select";
import SingleDatePicker from "components/SingleDatePicker";
import { PageLayout, PageTitle } from "components/layout";
import { Button } from "components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { useGetStoresOptionsQuery } from "@/services/store/select-options";
import {
  useExportSoldItemsExcelMutation,
  useExportSoldItemsPdfMutation,
  useGetSoldItemsAllowedStoresQuery,
} from "@/services/sold-items";
import { layoutClasses } from "@/utils/css-classes";
import { formatOptionLabel } from "@/utils/format-option-label";

import {
  EMAIL_PLACEHOLDER,
  GLOBALLY_HANDLED_STATUS,
  SOLD_ITEMS_LABELS,
  SOLD_ITEMS_MESSAGES,
  type ExportKind,
} from "./types";

export default function SoldItemsListingPage() {
  const [storeCode, setStoreCode] = useState<number | undefined>(undefined);
  const [date, setDate] = useState<Date | null>(null);

  const { data: storeOptions } = useGetStoresOptionsQuery(undefined, {
    refetchOnMountOrArgChange: false,
  });
  const { data: allowedStores } = useGetSoldItemsAllowedStoresQuery();

  const allowedCodes: number[] | null = allowedStores?.storeCodes ?? null;

  const availableStores = useMemo(() => {
    const all = storeOptions ?? [];
    if (allowedCodes === null) return all;
    return all.filter((option: { value: string }) =>
      allowedCodes.includes(Number(option.value)),
    );
  }, [storeOptions, allowedCodes]);

  const isStoreLocked: boolean =
    allowedCodes !== null && availableStores.length === 1;

  useEffect(() => {
    if (isStoreLocked && storeCode === undefined) {
      setStoreCode(Number(availableStores[0].value));
    }
  }, [isStoreLocked, storeCode, availableStores]);

  const [exportExcel, { isLoading: exportingExcel }] =
    useExportSoldItemsExcelMutation();
  const [exportPdf, { isLoading: exportingPdf }] =
    useExportSoldItemsPdfMutation();

  const hasFilters: boolean = storeCode !== undefined && date !== null;
  const isExporting: boolean = exportingExcel || exportingPdf;

  const handleExport = async (kind: ExportKind): Promise<void> => {
    if (storeCode === undefined || date === null) {
      toast(SOLD_ITEMS_MESSAGES.MISSING_FILTERS);
      return;
    }

    const formattedDate: string = format(date, "yyyy-MM-dd");

    try {
      const params = { storeCode, date: formattedDate };
      const result =
        kind === "excel"
          ? await exportExcel(params).unwrap()
          : await exportPdf(params).unwrap();
      const queued = result as ISoldItemsExportQueued;

      if (queued?.jobId === undefined) {
        toast(SOLD_ITEMS_MESSAGES.EMPTY);
        return;
      }

      toast({
        description: SOLD_ITEMS_MESSAGES.EXPORT_QUEUED.description.replace(
          EMAIL_PLACEHOLDER,
          queued.email,
        ),
      });
    } catch (error) {
      const status = (error as { status?: number | string })?.status;
      if (!GLOBALLY_HANDLED_STATUS.has(status as number)) {
        toast(SOLD_ITEMS_MESSAGES.ERROR);
      }
    }
  };

  const currentStoreOption =
    availableStores.find(
      (option: { value: string }) => Number(option.value) === storeCode,
    ) ?? null;

  const handleStoreChange = (newValue: unknown): void => {
    const option = newValue as SingleValue<{ value: string }>;
    const parsed: number = Number(option?.value);
    setStoreCode(option?.value && !Number.isNaN(parsed) ? parsed : undefined);
  };

  return (
    <PageLayout>
      <div className={layoutClasses.pageContainer}>
        <PageTitle
          title={SOLD_ITEMS_LABELS.pageTitle}
          description={SOLD_ITEMS_LABELS.pageDescription}
          titleSize="text-3xl"
          descriptionSize="text-xl"
        />

        <div className="flex flex-col lg:flex-row lg:items-end gap-4 mt-4">
          <div className="w-64">
            <label className="text-xs text-slate-500 mb-1 block">
              {SOLD_ITEMS_LABELS.storeLabel}
            </label>
            <SelectInput
              options={availableStores}
              value={currentStoreOption}
              onChange={handleStoreChange}
              isClearable={!isStoreLocked}
              isDisabled={isStoreLocked}
              placeholder={SOLD_ITEMS_LABELS.storePlaceholder}
              formatOptionLabel={formatOptionLabel}
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs text-slate-500 mb-1 block">
              {SOLD_ITEMS_LABELS.dateLabel}
            </label>
            <SingleDatePicker
              date={date}
              onDateChange={setDate}
              placeholder={SOLD_ITEMS_LABELS.datePlaceholder}
              buttonClassName="w-64 h-8 min-h-[32px] rounded-md border border-solid border-[#d1d5db] bg-white text-slate-900 hover:bg-white text-sm font-normal"
            />
          </div>

          <div className="flex gap-3">
            <Button
              onClick={() => handleExport("excel")}
              disabled={!hasFilters || isExporting}
              className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              {exportingExcel
                ? SOLD_ITEMS_LABELS.generating
                : SOLD_ITEMS_LABELS.excelButton}
            </Button>
            <Button
              onClick={() => handleExport("pdf")}
              disabled={!hasFilters || isExporting}
              className="h-8 bg-rose-600 hover:bg-rose-700 text-white"
            >
              <FileText className="w-4 h-4 mr-2" />
              {exportingPdf
                ? SOLD_ITEMS_LABELS.generating
                : SOLD_ITEMS_LABELS.pdfButton}
            </Button>
          </div>
        </div>

        {!hasFilters && (
          <p className="text-xs text-slate-500 mt-2">
            {SOLD_ITEMS_LABELS.selectFiltersHint}
          </p>
        )}
      </div>
    </PageLayout>
  );
}
